import type { AdminDefaults } from '../types';

export function Settings({ defaults, setDefaults }: {
  defaults: AdminDefaults;
  setDefaults: React.Dispatch<React.SetStateAction<AdminDefaults>>;
}) {
  const d = defaults;
  const upd = (path: string, val: unknown) => {
    setDefaults(prev => {
      const n = structuredClone(prev);
      const keys = path.split('.');
      let o: Record<string, unknown> = n as unknown as Record<string, unknown>;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]] as Record<string, unknown>;
      o[keys[keys.length - 1]] = val;
      return n as unknown as AdminDefaults;
    });
  };

  const capField = (tier: 'free' | 'approved', label: string, badge: React.ReactNode) => (
    <div className="card" style={{ padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
        {label} {badge}
      </div>
      <div className="capfields">
        <div className="field" style={{ marginTop: 0 }}>
          <label>Token cap</label>
          <div className="inp-affix">
            <input type="number" value={Math.round(d[tier].tokens / 1000)} step={10}
              onChange={e => upd(tier + '.tokens', (+e.target.value || 0) * 1000)} />
            <span className="suf">k / mo</span>
          </div>
        </div>
        <div className="field" style={{ marginTop: 0 }}>
          <label>Cost cap</label>
          <div className="inp-affix">
            <span className="pre">£</span>
            <input type="number" value={d[tier].cost} step={0.5}
              onChange={e => upd(tier + '.cost', +e.target.value || 0)} />
            <span className="suf">/ mo</span>
          </div>
        </div>
      </div>
    </div>
  );

  const row = (title: string, desc: string, ctl: React.ReactNode) => (
    <div className="setrow">
      <div className="tx"><div className="t">{title}</div><div className="d">{desc}</div></div>
      <div className="ctl">{ctl}</div>
    </div>
  );

  const toggle = (on: boolean, fn: () => void) => (
    <button className={'toggle' + (on ? ' on' : '')} onClick={fn} />
  );

  const numInp = (val: number, suf: string, fn: (v: number) => void, pre?: string) => (
    <div className="inp-affix" style={{ width: 120 }}>
      {pre && <span className="pre">{pre}</span>}
      <input type="number" value={val} onChange={e => fn(+e.target.value || 0)} />
      {suf && <span className="suf">{suf}</span>}
    </div>
  );

  return (
    <>
      <div className="sec-h"><h2>Default AI caps</h2></div>
      <p style={{ color: 'var(--soft)', maxWidth: 640, margin: '-6px 2px 16px', fontSize: 14 }}>
        Applied to new accounts. Per-user overrides are set from the user drawer.
        When a cap is hit, AI generation pauses — capture and recall keep working.
      </p>
      <div className="grid2 even">
        {capField('free', 'Free tier',
          <span className="badge acc" style={{ textTransform: 'none' }}>default on register</span>)}
        {capField('approved', 'Approved tier',
          <span className="badge info" style={{ textTransform: 'none' }}>on approval</span>)}
      </div>

      <div className="sec-h"><h2>Registration</h2></div>
      <div className="card">
        {row('Require admin approval',
          'New sign-ups land in the approval queue. Off = everyone is auto-approved on register.',
          toggle(d.requireApproval, () => upd('requireApproval', !d.requireApproval)))}
        {row('Free-tier access before approval',
          'Let unapproved users use Jotly on the free-tier AI cap while they wait.',
          toggle(d.freeBeforeApproval, () => upd('freeBeforeApproval', !d.freeBeforeApproval)))}
      </div>

      <div className="sec-h"><h2>Alerting thresholds</h2><span className="c">when to raise a misuse flag</span></div>
      <div className="card">
        {row('Token-cap warning', 'Flag an account once it reaches this share of its token cap.',
          numInp(d.warnPct, '%', v => upd('warnPct', v)))}
        {row('Cost-spike multiple', "Flag when spend exceeds the account's own baseline by this multiple.",
          numInp(d.spikeX, '×', v => upd('spikeX', v)))}
        {row('Request-rate ceiling', 'Flag possible scripting above this many requests per minute.',
          numInp(d.ratePerMin, '/min', v => upd('ratePerMin', v)))}
        {row('Failed-login limit', 'Flag after this many failed sign-ins in 24h.',
          numInp(d.failMax, '', v => upd('failMax', v)))}
      </div>
    </>
  );
}
